import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { CATEGORY_LABELS, formatApiError, formatDate } from "@/lib/api";
import { Loading } from "@/components/common";
import { Action, EmptyState, PageHeader, Panel, StatusBadge, Tag } from "./ui";

export default function PortalGrants() {
  const { api, user } = useAuth();
  const [grants, setGrants] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/access-grants");
      setGrants(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setGrants([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revoke = async (id) => {
    try {
      const { data } = await api.post(`/portal/access-grants/${id}/revoke`);
      toast.success(
        data.sessions_revoked
          ? "Vínculo revogado. Sessões da rede de apoio encerradas imediatamente."
          : "Vínculo revogado."
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const canRevoke = ["clinical_coordinator", "admin"].includes(user?.role);

  return (
    <div data-testid="portal-grants-page" className="space-y-7">
      <PageHeader
        kicker="Autorizações por vínculo"
        title="Vínculos"
        subtitle="Cada chamada clínica revalida o vínculo no backend. Ao revogar, convites pendentes são invalidados e a sessão da pessoa é encerrada se não houver outro vínculo ativo."
      />

      {grants === null ? (
        <Loading label="Carregando vínculos…" />
      ) : grants.length === 0 ? (
        <EmptyState
          testId="grants-empty-state"
          icon={Link2}
          title="Nenhum vínculo criado"
          description="Abra um paciente e cadastre uma pessoa da rede de apoio para gerar o primeiro vínculo e convite."
          action={
            <Link to="/portal/pacientes">
              <Action variant="secondary" size="sm">
                Ir para Pacientes
              </Action>
            </Link>
          }
        />
      ) : (
        <div className="space-y-4">
          {grants.map((g, i) => (
            <Panel
              key={g.id}
              testId={`grant-row-${g.id}`}
              className="er-rise flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-[17px] font-semibold text-ink-900">{g.support_name}</h3>
                  <StatusBadge tone={g.status === "active" ? "active" : "alert"} testId={`grant-status-${g.id}`}>
                    {g.status === "active" ? "Ativo" : "Revogado"}
                  </StatusBadge>
                </div>
                <p className="mt-1.5 text-[14.5px] text-ink-600">
                  {g.relationship} de{" "}
                  <Link to={`/portal/pacientes/${g.patient_id}`} className="font-semibold text-sage-700 underline">
                    {g.patient_name}
                  </Link>
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(g.categories || []).map((c) => (
                    <Tag key={c}>{CATEGORY_LABELS[c] || c}</Tag>
                  ))}
                </div>
                <p className="mt-3 text-[13.5px] text-ink-500">
                  Criado em {formatDate(g.created_at)}
                  {g.revoked_at ? ` · Revogado em ${formatDate(g.revoked_at)}` : ""}
                </p>
              </div>
              {g.status === "active" && canRevoke ? (
                <Action variant="danger" icon={Ban} data-testid={`grant-revoke-${g.id}`} onClick={() => revoke(g.id)}>
                  Revogar acesso
                </Action>
              ) : null}
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
